import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { InvalidateDate } from "../../../../../../../react-core/Datepicker/Resources/ContentContent";
import { NumberTextBoxEditor } from "../../../../../../../react-core/Html/NumberTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { addDays, addYears, stripToDay } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { datePickerOptions, processTypeEnum } from "../../Resource/Contents";
import { CappedFlexiTable } from "./CappedFlexiTable";
import { frequencyMap, getAssetsToSellTotalForIncomeFromIncome } from "./Helper/IncomePaymentHelper";
import { IncomeArrangementType, incomePaymentDetailsLabels, incomePaymentDetailsMessages } from "./Resource/Content";
import { DrawdownIncomeReviewDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeReviewDetails";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { Arrangements } from "../../../EditIncomeWithdrawalsWizard/Actions/Arrangements";
import { DateHelper } from "../../../../../../../react-core/Helpers/DateHelper";
import _ from "lodash";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";

type OnChangeType = "CappedModels" | "FlexiModels";
export interface CappedFlexiSectionProps extends IProcessComponentProps {
    processType?: processTypeEnum;
    data: Arrangements;
    income: DrawdownAnnualIncome;
    drawdownType: DrawdownType;
    onIncomeChange: (v) => void;
    onChange: (v: Partial<Record<OnChangeType, CappedModel[] | FlexiModel[] | undefined>>, type: OnChangeType) => void;
    modelData?: DrawdownModel;
    pensionProtection?: PensionProtectionDetails;
    drawdownIncomeReview?: DrawdownIncomeReviewDetails;
    onIncomeReviewChange?: (v) => void;
    isGADReview?: boolean;
    drawdownIncomeFlexiModel?:FlexiModel;
}

interface ICappedFlexiSectionState {
    showMinDateError: boolean;
}

export class CappedFlexiSection extends ProcessComponent<CappedFlexiSectionProps, ICappedFlexiSectionState> {
    constructor(props: any) {
        super(props);
        this.state = { showMinDateError: false };
    }

    private handleDrawdownIncomeChange(changes) {
        this.props.onIncomeChange(changes);
    }

    cappedFlexiTotals(): number {
        const cappedModels = this.props.data?.CappedModels;
        const cappedTotals = cappedModels
            ? cappedModels.reduce((sum, model) => {
                  return sum + (model.IncomePerPaymentForCurrentPensionYear || 0);
              }, 0)
            : 0;
        return MoneyHelper.roundNumberToDecimalPlaces(cappedTotals + this.flexiTotals(), 2);
    }

    flexiTotals() {
        const flexiModels = this.props.data?.FlexiModels;

        return flexiModels
            ? flexiModels.reduce((sum, model) => {
                  return sum + (model.IncomePerPayment || 0);
              }, 0)
            : 0;
    }

    updateValue = (index: number, value: FlexiModel | CappedModel, type: IncomeArrangementType) => {
        const { data, onChange } = this.props;
        const { CappedModels, FlexiModels } = data;
        const capped = type === IncomeArrangementType.Capped;
        const list = capped ? CappedModels : FlexiModels;
        if (list && list.length > index) {
            const updatedList = Objects.cloneObject(list);
            updatedList[index] = value;
            const type: OnChangeType = capped ? "CappedModels" : ("FlexiModels" as const);
            const change: Parameters<typeof onChange>[0] = {
                [type]: updatedList,
            };
            onChange(change, type);
        }
    };

    updateValueByKey = (index: number, key: string, value: number, type: IncomeArrangementType) => {
        const { data, onChange } = this.props;
        const { CappedModels, FlexiModels } = data;
        const capped = type === IncomeArrangementType.Capped;
        const list = capped ? CappedModels : FlexiModels;
        if (list && list.length > index) {
            const updatedList = Objects.cloneObject(list);
            updatedList[index][key] = value;

            const type: OnChangeType = capped ? "CappedModels" : ("FlexiModels" as const);
            const change: Parameters<typeof onChange>[0] = {
                [type]: updatedList,
            };

            onChange(change, type);
        }
    };

    getFlexiModels = () => {
        const { drawdownIncomeFlexiModel, data } = this.props;

        return drawdownIncomeFlexiModel != null ? [...data.FlexiModels, drawdownIncomeFlexiModel] : data.FlexiModels;
    }

    public render(): JSX.Element {
        const { data, processType, modelData, pensionProtection, isGADReview, income, drawdownIncomeFlexiModel} = this.props;
        const { CappedModels, FlexiModels } = data;
        return (
            <>
                {!isNullOrEmpty(CappedModels) && (
                    <CappedFlexiTable
                        data={CappedModels}
                        type={IncomeArrangementType.Capped}
                        onKeyValueChange={(index, key, value) =>
                            this.updateValueByKey(index, key, value, IncomeArrangementType.Capped)
                        }
                        onValueChange={(index, value) => this.updateValue(index, value, IncomeArrangementType.Capped)}
                        drawdown={modelData}
                        processType={processType}
                        pensionProtection={pensionProtection}
                        isGADReview={isGADReview}
                        income={income}
                    />
                )}

                {(!isNullOrEmpty(FlexiModels) || drawdownIncomeFlexiModel != null) && (
                    <CappedFlexiTable
                        data={this.getFlexiModels()}
                        type={IncomeArrangementType.Flexi}
                        onValueChange={(index, value) => {
                            if ((this.getFlexiModels()[index]?.PensionArrangementId ?? 0) == 0) {
                                //drawdown income
                                var drawdownAnnualIncome = _.cloneDeep(this.props.income);
                                drawdownAnnualIncome.AnnualAmount.Amount.Amount = value.IncomePerPayment * frequencyMap[drawdownAnnualIncome?.Frequency ?? Frequency.Monthly];
                                drawdownAnnualIncome.GrossIncomePerPayment.Amount = value.IncomePerPayment;
                                this.handleDrawdownIncomeChange(drawdownAnnualIncome);
                            }
                            this.updateValue(index, value, IncomeArrangementType.Flexi)
                        }}
                        onKeyValueChange={(index, key, value) => {
                            if ((this.getFlexiModels()[index]?.PensionArrangementId ?? 0) == 0) {
                                //drawdown income
                                var drawdownAnnualIncome = _.cloneDeep(this.props.income);
                                drawdownAnnualIncome.AnnualAmount.Amount.Amount = value * frequencyMap[drawdownAnnualIncome?.Frequency ?? Frequency.Monthly];
                                drawdownAnnualIncome.GrossIncomePerPayment.Amount = value;
                                this.handleDrawdownIncomeChange(drawdownAnnualIncome);
                            }
                            this.updateValueByKey(index, key, value, IncomeArrangementType.Flexi);
                        }}
                        drawdown={modelData}
                        processType={processType}
                        pensionProtection={pensionProtection}
                    />
                )}
                {/* {this.props.drawdownType == DrawdownType.Flexible && (
                    <>                      
                        <h3>Flexible income</h3>
                        <div className="row">
                            <div className="col-12">
                                {this.props.drawdownIncomeReview?.IncomeReviewDate &&
                                    JsonDateParser.toDate(this.props.drawdownIncomeReview?.IncomeReviewDate) >
                                        JsonDateParser.toDate(addYears(new Date(), 3)) && (
                                        <Alert
                                            alertType={AlertType.Warning}
                                            message={incomePaymentDetailsMessages.confirmIncomeReviewDate}
                                        />
                                    )}
                                <DatePicker
                                    formGroupCssWrapper={"control-size-430"}
                                    selected={
                                        this.props.drawdownIncomeReview?.IncomeReviewDate &&
                                        this.props.drawdownIncomeReview?.IncomeReviewDate.toString() != InvalidateDate
                                            ? JsonDateParser.toDateNoOffset(
                                                  this.props.drawdownIncomeReview?.IncomeReviewDate
                                              )
                                            : DateHelper.yearFromNow()
                                    }
                                    label={incomePaymentDetailsLabels.nextFlexiReviewDate}
                                    {...datePickerOptions}
                                    alwaysValidateOnUpdate={true}
                                    atLeast={{
                                        value: stripToDay(addDays(new Date(Date.now()), 20)),
                                        errorMessage: incomePaymentDetailsMessages.nextFlexiReviewDateUnder,
                                    }}
                                    filterDate={{
                                        errorMessage: incomePaymentDetailsMessages.dateOver28,
                                        validator: (v) => v.getDate() <= 28,
                                    }}
                                    required={incomePaymentDetailsMessages.nextFlexiReviewDateRequired}
                                    onChange={(selectedDate) => {
                                        if (!this.meets20DayRequirement(selectedDate)) {
                                            this.setState({ showMinDateError: true });
                                            const defaultDate = addDays(new Date(), 20);
                                            this.props.onIncomeReviewChange({
                                                IncomeReviewDate: JsonDateParser.toJsonDateNoOffset(defaultDate),
                                            });
                                        } else {
                                            this.setState({ showMinDateError: false });
                                            this.props.onIncomeReviewChange({
                                                IncomeReviewDate: JsonDateParser.toJsonDateNoOffset(selectedDate),
                                            });
                                        }
                                    }}
                                    appendErrors={
                                        this.state.showMinDateError
                                            ? [incomePaymentDetailsMessages.nextFlexiReviewDateUnder]
                                            : []
                                    }
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <NumberTextBoxEditor
                                    cssWrapper="control-size-430"
                                    value={this.props.drawdownIncomeReview?.IncomeReviewFrequencyCount ?? 12}
                                    moreThan={{
                                        errorMessage:
                                            incomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 0,
                                    }}
                                    atMost={{
                                        errorMessage:
                                            incomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 60,
                                    }}
                                    required={
                                        this.props.drawdownType == DrawdownType.Flexible
                                            ? incomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver
                                            : null
                                    }
                                    label={incomePaymentDetailsLabels.frequencyOfFlexiIncomeReview}
                                    onChange={(v) =>
                                        this.props.onIncomeReviewChange({
                                            IncomeReviewFrequencyCount: v,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </>
                )} */}
            </>
        );
    }

    private meets20DayRequirement(selectedDate: Date) {
        const minDate = stripToDay(addDays(new Date(Date.now()), 20));
        return selectedDate >= minDate;
    }
}
